// SVG document → symbol geometry. Walks the same SVG the canvas renders and
// produces flattened polylines in block-local space (centered on the viewBox
// center, Y up) so each distinct SVG becomes exactly one DXF BLOCK.
//
// Scope matches the shipped asset set: line/rect(+rx)/circle/ellipse/path/
// polygon/polyline shapes, <g> nesting with transform + class inheritance,
// <style> class blocks, and defs/clipPath/mask subtrees (skipped — clip and
// mask effects are not representable in R12; shapes render unclipped).

import { IDENTITY, applyMat, blockLocalMatrix, multiply, parseSvgTransform } from './transform2d';
import { FLATTEN_TOLERANCE, dedupePoints, flattenPathData } from './svgPath';
import { INITIAL_PAINT, isVisiblePaint, parseStylesheet, resolvePaint } from './svgStyle';

/** Non-rendered subtrees: definitions, metadata, and paint servers. */
const SKIP_TAGS = new Set([
  'defs', 'style', 'clippath', 'mask', 'title', 'desc', 'metadata', 'symbol',
  'marker', 'pattern', 'filter', 'lineargradient', 'radialgradient', 'script',
]);
const CONTAINER_TAGS = new Set(['g', 'a', 'svg']);
const SHAPE_TAGS = new Set(['line', 'rect', 'circle', 'ellipse', 'path', 'polygon', 'polyline']);

const num = (el, name, fallback = 0) => {
  const raw = el.getAttribute(name);
  if (raw == null) return fallback;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : fallback;
};

/** Closed polyline approximation of an ellipse, honoring the flatten tolerance. */
export function sampleEllipse(cx, cy, rx, ry, tol = FLATTEN_TOLERANCE) {
  const rMax = Math.max(Math.abs(rx), Math.abs(ry));
  if (rMax === 0) return [];
  const maxStep = Math.min(Math.PI / 8, 2 * Math.acos(Math.max(-1, Math.min(1, 1 - tol / rMax))));
  const steps = Math.max(16, Math.ceil((2 * Math.PI) / Math.max(maxStep, 1e-6)));
  return Array.from({ length: steps }, (_, i) => {
    const theta = (2 * Math.PI * i) / steps;
    return { x: cx + rx * Math.cos(theta), y: cy + ry * Math.sin(theta) };
  });
}

function rectSubPaths(el) {
  const x = num(el, 'x');
  const y = num(el, 'y');
  const w = num(el, 'width');
  const h = num(el, 'height');
  if (w <= 0 || h <= 0) return [];
  let rx = num(el, 'rx', NaN);
  let ry = num(el, 'ry', NaN);
  if (Number.isNaN(rx) && Number.isNaN(ry)) {
    rx = 0;
    ry = 0;
  } else {
    if (Number.isNaN(rx)) rx = ry;
    if (Number.isNaN(ry)) ry = rx;
  }
  rx = Math.max(0, Math.min(rx, w / 2));
  ry = Math.max(0, Math.min(ry, h / 2));
  if (rx === 0 || ry === 0) {
    return [{ points: [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }], closed: true }];
  }
  // Rounded rect: synthesize path data so corner arcs reuse the tested arc flattener.
  const d = [
    `M ${x + rx} ${y}`,
    `H ${x + w - rx}`,
    `A ${rx} ${ry} 0 0 1 ${x + w} ${y + ry}`,
    `V ${y + h - ry}`,
    `A ${rx} ${ry} 0 0 1 ${x + w - rx} ${y + h}`,
    `H ${x + rx}`,
    `A ${rx} ${ry} 0 0 1 ${x} ${y + h - ry}`,
    `V ${y + ry}`,
    `A ${rx} ${ry} 0 0 1 ${x + rx} ${y}`,
    'Z',
  ].join(' ');
  return flattenPathData(d);
}

function pointListSubPaths(el, closed) {
  const raw = el.getAttribute('points') || '';
  const values = (raw.match(/-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || []).map(Number);
  const points = [];
  for (let i = 0; i + 1 < values.length; i += 2) points.push({ x: values[i], y: values[i + 1] });
  const deduped = dedupePoints(points);
  return deduped.length >= 2 ? [{ points: deduped, closed }] : [];
}

function shapeSubPaths(tag, el) {
  switch (tag) {
    case 'line': {
      const points = dedupePoints([
        { x: num(el, 'x1'), y: num(el, 'y1') },
        { x: num(el, 'x2'), y: num(el, 'y2') },
      ]);
      return points.length === 2 ? [{ points, closed: false }] : [];
    }
    case 'rect':
      return rectSubPaths(el);
    case 'circle': {
      const r = num(el, 'r');
      const points = sampleEllipse(num(el, 'cx'), num(el, 'cy'), r, r);
      return points.length >= 3 ? [{ points, closed: true }] : [];
    }
    case 'ellipse': {
      const points = sampleEllipse(num(el, 'cx'), num(el, 'cy'), num(el, 'rx'), num(el, 'ry'));
      return points.length >= 3 ? [{ points, closed: true }] : [];
    }
    case 'path':
      return flattenPathData(el.getAttribute('d') || '');
    case 'polygon':
      return pointListSubPaths(el, true);
    case 'polyline':
      return pointListSubPaths(el, false);
    default:
      return [];
  }
}

function parseViewBox(root) {
  const raw = root.getAttribute('viewBox');
  if (raw) {
    const values = (raw.match(/-?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) || []).map(Number);
    if (values.length === 4 && values[2] > 0 && values[3] > 0) {
      return { minX: values[0], minY: values[1], width: values[2], height: values[3] };
    }
    return null;
  }
  const width = num(root, 'width', NaN);
  const height = num(root, 'height', NaN);
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { minX: 0, minY: 0, width, height };
  }
  return null;
}

function parseIntrinsic(root) {
  const widthRaw = root.getAttribute('width');
  const heightRaw = root.getAttribute('height');
  if (!widthRaw || !heightRaw || widthRaw.includes('%') || heightRaw.includes('%')) return null;
  const width = Number.parseFloat(widthRaw);
  const height = Number.parseFloat(heightRaw);
  return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 ? { width, height } : null;
}

const isFinitePolyline = (points) => points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y));

const NAMED_COLORS = {
  black: [0, 0, 0],
  white: [255, 255, 255],
  red: [255, 0, 0],
  green: [0, 128, 0],
  lime: [0, 255, 0],
  blue: [0, 0, 255],
  cyan: [0, 255, 255],
  aqua: [0, 255, 255],
  magenta: [255, 0, 255],
  fuchsia: [255, 0, 255],
  yellow: [255, 255, 0],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  silver: [192, 192, 192],
};

const ACI_PALETTE = [
  { aci: 7, r: 0, g: 0, b: 0 },
  { aci: 8, r: 128, g: 128, b: 128 },
  { aci: 9, r: 192, g: 192, b: 192 },
  { aci: 1, r: 255, g: 0, b: 0 },
  { aci: 2, r: 255, g: 255, b: 0 },
  { aci: 3, r: 0, g: 255, b: 0 },
  { aci: 4, r: 0, g: 255, b: 255 },
  { aci: 5, r: 0, g: 0, b: 255 },
  { aci: 6, r: 255, g: 0, b: 255 },
];

const nearestAci = ([r, g, b]) => {
  let best = ACI_PALETTE[0];
  let bestDistance = Infinity;
  for (const candidate of ACI_PALETTE) {
    const distance =
      ((r - candidate.r) ** 2) +
      ((g - candidate.g) ** 2) +
      ((b - candidate.b) ** 2);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best.aci;
};

const parseCssColor = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || raw === 'none' || raw === 'transparent' || raw.startsWith('url(')) return null;
  if (NAMED_COLORS[raw]) return NAMED_COLORS[raw];
  const shortHex = /^#([0-9a-f]{3})$/i.exec(raw);
  if (shortHex) {
    return shortHex[1].split('').map((ch) => Number.parseInt(`${ch}${ch}`, 16));
  }
  const longHex = /^#([0-9a-f]{6})$/i.exec(raw);
  if (longHex) {
    return [0, 2, 4].map((offset) => Number.parseInt(longHex[1].slice(offset, offset + 2), 16));
  }
  const rgb = /^rgba?\(([^)]+)\)$/.exec(raw);
  if (rgb) {
    const parts = rgb[1].split(',').map((part) => Number.parseFloat(part));
    if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) {
      return parts.slice(0, 3).map((part) => Math.max(0, Math.min(255, Math.round(part))));
    }
  }
  return null;
};

const colorAci = (value) => {
  const rgb = parseCssColor(value);
  return rgb ? nearestAci(rgb) : null;
};

const strokeAci = (paint) => colorAci(paint.stroke);
const fillAci = (paint) => colorAci(paint.fill);
const hasFill = (paint) => fillAci(paint) !== null;

const closePolyline = (points) => {
  if (points.length === 0) return [];
  const first = points[0];
  const last = points[points.length - 1];
  if (Math.abs(first.x - last.x) < 1e-9 && Math.abs(first.y - last.y) < 1e-9) return points;
  return [...points, first];
};

const polylineBounds = (polys) => {
  const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  for (const poly of polys) {
    for (const p of poly.points) {
      bounds.minX = Math.min(bounds.minX, p.x);
      bounds.minY = Math.min(bounds.minY, p.y);
      bounds.maxX = Math.max(bounds.maxX, p.x);
      bounds.maxY = Math.max(bounds.maxY, p.y);
    }
  }
  return Number.isFinite(bounds.minX) ? bounds : null;
};

const scanlineIntersections = (polys, y) => {
  const xs = [];
  for (const poly of polys) {
    const points = closePolyline(poly.points);
    for (let i = 0; i + 1 < points.length; i++) {
      const a = points[i];
      const b = points[i + 1];
      if (Math.abs(a.y - b.y) < 1e-9) continue;
      const minY = Math.min(a.y, b.y);
      const maxY = Math.max(a.y, b.y);
      if (y < minY || y >= maxY) continue;
      const t = (y - a.y) / (b.y - a.y);
      xs.push(a.x + (t * (b.x - a.x)));
    }
  }
  return xs.sort((a, b) => a - b);
};

const fillStripsForPolylines = (polys, aci) => {
  if (!Number.isInteger(aci) || polys.length === 0) return [];
  const bounds = polylineBounds(polys);
  if (!bounds) return [];
  const height = bounds.maxY - bounds.minY;
  const width = bounds.maxX - bounds.minX;
  if (height <= 0 || width <= 0) return [];

  const step = Math.max(0.8, Math.min(width, height) / 260);
  const fills = [];
  for (let y0 = bounds.minY; y0 < bounds.maxY; y0 += step) {
    const y1 = Math.min(bounds.maxY, y0 + step);
    const y = (y0 + y1) / 2;
    const xs = scanlineIntersections(polys, y);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const x0 = xs[i];
      const x1 = xs[i + 1];
      if (!Number.isFinite(x0) || !Number.isFinite(x1) || Math.abs(x1 - x0) < 1e-6) continue;
      fills.push({
        aci,
        corners: [
          { x: x0, y: y0 },
          { x: x1, y: y0 },
          { x: x1, y: y1 },
          { x: x0, y: y1 },
        ],
      });
    }
  }
  return fills;
};

/**
 * Convert SVG text into block-local symbol geometry.
 * Throws on unparseable documents (caller emits the placeholder fallback).
 */
export function svgToSymbolGeometry(svgText) {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.localName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('not a parseable SVG document');
  }
  const viewBox = parseViewBox(root);
  if (!viewBox) throw new Error('SVG has no usable viewBox or width/height');

  const styleNodes = Array.from(doc.getElementsByTagName('style'));
  const rules = parseStylesheet(styleNodes.map((s) => s.textContent || '').join('\n'));

  const polylines = [];
  const fills = [];
  const unsupported = new Set();

  const walk = (el, matrix, paint) => {
    for (const child of Array.from(el.children)) {
      const tag = child.localName.toLowerCase();
      if (SKIP_TAGS.has(tag)) continue;
      const childPaint = resolvePaint(child, rules, paint);
      const childMatrix = multiply(matrix, parseSvgTransform(child.getAttribute('transform')));
      if (CONTAINER_TAGS.has(tag)) {
        walk(child, childMatrix, childPaint);
        continue;
      }
      if (!SHAPE_TAGS.has(tag)) {
        unsupported.add(tag);
        continue;
      }
      if (!isVisiblePaint(childPaint)) continue;
      const childPolys = [];
      for (const sub of shapeSubPaths(tag, child)) {
        const mapped = sub.points.map((p) => applyMat(childMatrix, p));
        if (!isFinitePolyline(mapped)) {
          unsupported.add(`${tag} (non-finite geometry)`);
          continue;
        }
        const poly = { points: mapped, closed: sub.closed, aci: strokeAci(childPaint) ?? fillAci(childPaint) };
        polylines.push(poly);
        if (sub.closed) childPolys.push(poly);
      }
      if (hasFill(childPaint)) fills.push(...fillStripsForPolylines(childPolys, fillAci(childPaint)));
    }
  };

  const local = blockLocalMatrix(viewBox);
  const rootMatrix = multiply(multiply(IDENTITY, local), parseSvgTransform(root.getAttribute('transform')));
  walk(root, rootMatrix, resolvePaint(root, rules, INITIAL_PAINT));

  return { viewBox, intrinsic: parseIntrinsic(root), polylines, fills, unsupported: Array.from(unsupported) };
}
